import { Link } from "react-router-dom";
import { APP_TABS, Brand } from "@/lib/nav";
import { Moon, ListChecks, HeartHandshake, CalendarClock, ArrowRight, Sparkles } from "lucide-react";

const FEATURES = [
  { icon: Sparkles, title: "A capacity score, not a to-do pile", body: "Every morning Ebbly reads your cycle phase, sleep and mood to tell you how much you realistically have in the tank — from Rest to Push." },
  { icon: ListChecks, title: "Your Todoist, re-sorted for today", body: "Tasks that fit today's energy float to the top as your Top 3. Heavy ones get gently flagged 'maybe skip'. Postpone or check off in a tap." },
  { icon: Moon, title: "Synced with your Ultrahuman ring", body: "Sleep, HRV and recovery pulled automatically to shape your day — with a manual slider whenever you want it." },
  { icon: HeartHandshake, title: "Kind, ADHD-friendly by design", body: "Low-capacity days come with a warm reframe, so pacing feels like smart planning — never falling behind." },
];

// --- Phone mockups (styled, on-brand) ---
const PhoneFrame = ({ children, className = "" }) => (
  <div className={`relative w-[230px] rounded-[2.2rem] bg-stone-900 p-2.5 shadow-[0_30px_60px_-15px_rgba(75,79,41,0.45)] ${className}`}>
    <div className="rounded-[1.7rem] overflow-hidden bg-[#e7d2c4] h-[470px]">
      <div className="h-6 flex items-center justify-center">
        <div className="h-1.5 w-16 rounded-full bg-stone-300/70" />
      </div>
      <div className="px-4">{children}</div>
    </div>
  </div>
);

const CheckinMock = () => (
  <div>
    <p className="text-[9px] uppercase tracking-[0.25em] text-[#8a6f64]">Ebbly</p>
    <h4 className="font-heading text-xl font-semibold text-[#b1543b] mb-4">Morning check-in</h4>
    <div className="rounded-2xl bg-white p-4 shadow-sm space-y-4">
      <div>
        <p className="text-[9px] uppercase tracking-widest text-stone-400 mb-1.5">Cycle day</p>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-stone-100" />
          <div className="flex-1 h-9 rounded-xl border border-stone-200 flex items-center justify-center font-heading font-semibold text-stone-700">24</div>
          <div className="h-8 w-8 rounded-full bg-stone-100" />
        </div>
      </div>
      <div>
        <p className="text-[9px] uppercase tracking-widest text-stone-400 mb-1.5">Mood</p>
        <div className="grid grid-cols-5 gap-1.5">
          {["😞","😕","😐","🙂","😄"].map((e,i)=>(
            <div key={e} className={`h-8 rounded-lg flex items-center justify-center text-sm ${i===3?"bg-[#b1543b]":"bg-white border border-stone-100"}`}>{e}</div>
          ))}
        </div>
      </div>
      <div className="h-10 rounded-full bg-[#b1543b] flex items-center justify-center text-white text-xs font-semibold">Show me today's capacity</div>
    </div>
  </div>
);

const CapacityMock = () => (
  <div>
    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 text-center">
      <p className="text-[8px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-1">Luteal phase · day 24</p>
      <div className="font-heading text-5xl font-medium text-amber-900 leading-none">61%</div>
      <p className="font-heading text-lg font-bold text-amber-900 mt-1">Steady</p>
      <p className="font-heading italic text-amber-700 text-xs mt-1">steady hands, steady heart</p>
    </div>
    <div className="rounded-2xl bg-white p-4 shadow-sm mt-3">
      <p className="font-heading text-sm font-bold text-[#b1543b] mb-2">Top 3 for today</p>
      {["Finish report draft","Reply to emails","10 min stretch"].map((t)=>(
        <div key={t} className="flex items-center gap-2 py-1.5 border-b border-stone-50 last:border-0">
          <div className="h-4 w-4 rounded-full border-2 border-stone-300" />
          <span className="text-xs text-stone-700 truncate">{t}</span>
        </div>
      ))}
    </div>
  </div>
);

export const Landing = () => {
  return (
    <div className="min-h-screen bg-[#e7d2c4] text-[#3b2a24]" data-testid="landing-page">
      {/* Top menu header */}
      <header className="sticky top-0 z-30 bg-[#e7d2c4]/85 backdrop-blur-xl border-b border-[#d6bfae]">
        <div className="mx-auto max-w-6xl px-5 h-16 flex items-center justify-between">
          <Brand />
          <nav className="flex items-center gap-1 sm:gap-2">
            {APP_TABS.map(({ id, label }) => (
              <Link
                key={id}
                to={`/app/${id}`}
                data-testid={`landing-nav-${id}`}
                className="hidden sm:inline px-3 py-2 rounded-full text-sm font-medium text-stone-600 hover:text-[#b1543b] hover:bg-white/60 transition-colors"
              >
                {label}
              </Link>
            ))}
            <Link
              to="/app/today"
              data-testid="landing-open-app"
              className="ml-1 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#b1543b] hover:bg-[#8f3f2b] text-white text-sm font-semibold transition-colors"
            >
              Open Ebbly <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-5 pt-14 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/70 border border-[#d6bfae] text-xs font-semibold uppercase tracking-[0.2em] text-[#8a6f64] mb-6">
            For women with ADHD
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold text-[#3b2a24] leading-[1.02] tracking-tight">
            Plan your day around the energy you <span className="italic text-[#b1543b]">actually</span> have.
          </h1>
          <p className="mt-6 text-lg text-stone-600 max-w-xl">
            Ebbly is a daily adaptive capacity planner. Each morning it reads where you are in your cycle,
            how you slept, and your mood — then syncs your sleep tracker, calendar and to-do list into one
            gentle plan that fits the day you're really in.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/app/today"
              data-testid="hero-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#b1543b] hover:bg-[#8f3f2b] text-white text-base font-semibold transition-colors"
            >
              Start your check-in <ArrowRight className="h-5 w-5" />
            </Link>
            <span className="text-sm text-stone-500">No sign-up · your data stays yours</span>
          </div>
          <div className="mt-8 flex items-center gap-5 text-xs font-medium text-stone-500">
            <span className="flex items-center gap-1.5"><Moon className="h-4 w-4 text-[#b1543b]" /> Ultrahuman</span>
            <span className="flex items-center gap-1.5"><ListChecks className="h-4 w-4 text-[#b1543b]" /> Todoist</span>
            <span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-[#b1543b]" /> Calendar</span>
          </div>
        </div>

        {/* Phone mockups */}
        <div className="relative flex justify-center lg:justify-end items-start gap-4 pt-6">
          <PhoneFrame className="rotate-[-5deg] mt-10 hidden sm:block"><CheckinMock /></PhoneFrame>
          <PhoneFrame className="rotate-[4deg] z-10"><CapacityMock /></PhoneFrame>
        </div>
      </section>

      {/* Features */}
      <section className="bg-[#efe1d8] border-y border-[#d6bfae]">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#3b2a24] text-center max-w-2xl mx-auto">
            One calm home screen instead of ten anxious tabs
          </h2>
          <div className="mt-14 grid sm:grid-cols-2 gap-6">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-3xl bg-[#e7d2c4] border border-[#d6bfae] p-8">
                <div className="h-12 w-12 rounded-2xl bg-[#b1543b] flex items-center justify-center mb-5">
                  <Icon className="h-6 w-6 text-[#e9e59a]" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#3b2a24] mb-2">{title}</h3>
                <p className="text-[#5c4a41]">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mx-auto max-w-3xl px-5 py-24 text-center">
        <h2 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3b2a24] leading-tight">
          Some days you push. Some days you <span className="italic text-[#b1543b]">rest</span>. Ebbly knows the difference.
        </h2>
        <Link
          to="/app/today"
          data-testid="closing-cta"
          className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#b1543b] hover:bg-[#8f3f2b] text-white text-lg font-semibold transition-colors"
        >
          Open Ebbly <ArrowRight className="h-5 w-5" />
        </Link>
        <p className="mt-16 text-sm text-stone-400">
          Ebbly is a personal wellness aid for planning — not medical advice, diagnosis, or birth control.
        </p>
      </section>
    </div>
  );
};
